// Seat a just-focused box right above the on-screen keyboard (founder,
// 2026-10-25: the message box opened under the keyboard; the bid box rose
// further than it needed to).
//
// Wait for the keyboard to settle — the visual viewport shrinks once on iOS
// Safari, in steps on Android Chrome, and the webview itself shrinks in the
// app (Keyboard.resize = native) — then scroll the page so `el`'s bottom sits
// `gap` px above the keyboard's top edge, moving up OR down. The box never
// goes under the fixed header, and the scroll stays out of the last
// keyboard-inset's worth of range: there iOS pans the visual viewport and
// drags the header under the status-bar clock (measured 2026-09-28, see
// SongSearchFeed's rise). `el` is the box around the focused field; an
// optional `through` element below it comes up too when there's room. With
// no keyboard (a desktop, a hardware keyboard) a box is only scrolled to if
// something covers it.

const SETTLE_MS = 120;
const FALLBACK_MS = 700;
const KEYBOARD_MIN = 100;

let probe: HTMLDivElement | null = null;
function probeTop(): number {
  if (!probe) {
    probe = document.createElement("div");
    probe.setAttribute("aria-hidden", "true");
    probe.style.cssText = "position:fixed;top:0;left:0;width:0;height:0;pointer-events:none;visibility:hidden";
    document.body.appendChild(probe);
  }
  return probe.getBoundingClientRect().top;
}

let room: HTMLDivElement | null = null;
function addRoom(el: HTMLElement, px: number) {
  if (!room) {
    room = document.createElement("div");
    room.setAttribute("aria-hidden", "true");
    room.dataset.keyboardRoom = "";
    document.body.appendChild(room);
  }
  room.style.height = `${(parseFloat(room.style.height) || 0) + px}px`;
  // Gone once the box no longer has the keyboard: on blur, or — when the box
  // is removed while focused (its ×), which fires no blur in some browsers —
  // on the viewport change of the keyboard closing.
  const vv = window.visualViewport;
  const done = () => {
    if (el.isConnected && el.contains(document.activeElement)) return;
    room?.remove();
    room = null;
    document.removeEventListener("focusout", later);
    vv?.removeEventListener("resize", done);
  };
  const later = () => setTimeout(done, 0);
  document.addEventListener("focusout", later);
  vv?.addEventListener("resize", done);
}

export function revealAboveKeyboard(el: HTMLElement | null, gap = 12, through?: HTMLElement | null): () => void {
  const vv = window.visualViewport;
  if (!el || !vv) return () => {};
  const startHeight = vv.height;
  let settle: ReturnType<typeof setTimeout> | undefined;

  const seat = () => {
    stop();
    // Gone, or the fan already left the box (a scroll-dismissed keyboard must
    // not yank the page).
    if (!el.isConnected || !el.contains(document.activeElement)) return;
    const keyboardUp = vv.height < startHeight - KEYBOARD_MIN || window.innerHeight - vv.height > KEYBOARD_MIN;
    const r = el.getBoundingClientRect();
    // Where the visible area is, in the same terms as `r`. WebKit measures
    // from the visible area (a fixed element at the layout top reads
    // −offsetTop), Chrome from the layout viewport (it reads 0); a fixed
    // probe tells them apart (measured on iOS 26: offsetTop 310 while the
    // visible top stayed at 0).
    const layoutTop = probeTop();
    const visibleTop = layoutTop + vv.offsetTop;
    const header = parseFloat(getComputedStyle(document.documentElement).getPropertyValue("--app-header-h")) || 0;
    // `through`: seat that element's bottom instead (the bid box brings the
    // pay button up with it), as long as `el` still clears the header.
    // Without a keyboard nothing is brought up beyond the box itself.
    const bottom = keyboardUp && through?.isConnected ? Math.max(r.bottom, through.getBoundingClientRect().bottom) : r.bottom;
    let move = bottom - (visibleTop + vv.height - gap);
    if (!keyboardUp && move <= 0) return;
    move = Math.min(move, r.top - Math.max(visibleTop, layoutTop + header) - 8);
    const doc = document.documentElement;
    const inset = Math.max(0, window.innerHeight - vv.height);
    const reach = () => doc.scrollHeight - window.innerHeight - inset;
    const want = Math.round(Math.max(0, window.scrollY + move));
    // A short page can't scroll that far: add room under it while the box
    // has the keyboard (the song search's rise does the same), gone on blur.
    const missing = want - reach();
    if (missing > 0) addRoom(el, missing);
    const top = Math.max(0, Math.min(want, reach()));
    if (Math.abs(top - window.scrollY) < 2) return;
    const still = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    window.scrollTo({ top, behavior: still ? "auto" : "smooth" });
  };
  const onResize = () => {
    clearTimeout(settle);
    settle = setTimeout(seat, SETTLE_MS);
  };
  const fallback = setTimeout(seat, FALLBACK_MS);
  const stop = () => {
    clearTimeout(settle);
    clearTimeout(fallback);
    vv.removeEventListener("resize", onResize);
  };
  vv.addEventListener("resize", onResize);
  return stop;
}
